import { ChangeDetectionStrategy, Component, OnInit, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslocoModule } from '@jsverse/transloco';
import { TooltipDirective } from '@catch/directives';
import { AvatarPipe } from '../../pipes/avatar.pipe';
import { illustration } from '../../../assets/animations/animations';
import { Store } from '@ngrx/store';
import { DateTime } from 'luxon';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AppState } from '../../app.reducer';
import { Notification } from '../../interfaces/notification';
import { FontawesomeService } from '../../services/fontawesome.service';
import { TopnavService } from '../../services/topnav.service';
import { toggleNotificationState } from '../../store/notifications/notifications.actions';
import {
  selectNotificationsAll,
  selectNotificationsLoading,
  selectNotificationsUnseen,
} from '../../store/notifications/notifications.reducer';
import { EmptyPlaceholderComponent } from '@catch/general-ui';
import { ButtonComponent } from '../button/button.component';
import { AvatarPlaceholderComponent } from '../avatar-placeholder/avatar-placeholder.component';

@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [illustration],
  imports: [CommonModule, RouterModule, FontAwesomeModule, TranslocoModule, TooltipDirective, AvatarPipe, EmptyPlaceholderComponent, ButtonComponent, AvatarPlaceholderComponent],
})
export class NotificationsComponent implements OnInit {
  /** Emitted when the user activates a notification (navigate); parent may close overlays. */
  readonly notificationNavigate = output<void>();

  notifications$!: Observable<Notification[]>;
  notificationsLoading$!: Observable<boolean>;
  notificationsUnseen$!: Observable<number>;

  constructor(
    public fontawesomeService: FontawesomeService,
    private topnavService: TopnavService,
    private store: Store<AppState>
  ) {}

  ngOnInit(): void {
    this.notifications$ = this.store.select(selectNotificationsAll);
    this.notificationsLoading$ = this.store.select(selectNotificationsLoading);
    this.notificationsUnseen$ = this.store.select(selectNotificationsUnseen);
  }

  getAvatarURL(applicationID: number) {
    return `${environment.apiBaseUrl}/company/applications/${applicationID}/avatar`;
  }

  getInitials(fullName?: string): string {
    const nameParts = (fullName ?? '').trim().split(/\s+/).filter(Boolean);
    const firstInitial = nameParts[0]?.[0] ?? '';
    const lastInitial = nameParts.length > 1 ? nameParts[nameParts.length - 1]?.[0] ?? '' : '';
    return `${firstInitial}${lastInitial}`.toUpperCase();
  }

  timestampDifference(notification: Notification) {
    return (-DateTime.fromISO(notification.applicationTimestamp).diffNow('days').days).toFixed(0);
  }

  openCompanyChat(notification: Notification): void {
    if (notification.type === 'NewCommentNotification' && !notification.jobID && !notification.applicationID) {
      this.topnavService.openChat.emit();
    }
  }

  generateRouterLink(notification: Notification) {
    switch (notification.type) {
      case 'NewApplicationNotification':
      case 'WorkflowNotification':
      case 'ApplicationUpdatedNotification': {
        return ['/jobs', notification.jobID, 'applications', notification.applicationID];
      }
      case 'NewJobNotification': {
        return ['/jobs', notification.jobID];
      }
      case 'NewCommentNotification': {
        if (!notification.jobID && !notification.applicationID) return null;
        return ['/jobs', notification.jobID ?? 'talents', 'applications', notification.applicationID];
      }
      case 'NewEMailNotification': {
        return ['/jobs', notification.jobID, 'applications', notification.applicationID];
      }
      case 'ReminderNotification': {
        return ['/jobs', notification.jobID, 'applications', notification.applicationID];
      }
      default: {
        return null;
      }
    }
  }

  toggleNotificationState(notification: Notification, targetState: boolean = null) {
    if (targetState && notification.seen === targetState) return;
    this.store.dispatch(
      toggleNotificationState({
        notification,
      })
    );
  }
}
